import type { ReviewOption } from '@iheartjane/menu';
import { FEELING_ICONS } from '@iheartjane/menu';
import type { Nullish } from '@iheartjane/types';
import { getCookieFromDocument, setCookie } from '@iheartjane/utils';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFetcher } from 'react-router';

import { ClientOnly } from '@jane/bloom/components/clientOnly';
import type { ActionResult } from '@jane/bloom/types/remixAction';
import { durations } from '@jane/bloom/utils/durations';
import { PROMPTS, getPromptCookieName } from '@jane/bloom/utils/prompts';
import type {
  ReviewTagOption,
  ReviewTagOptions,
} from '@jane/bloom/viewModels/reviewTagOptions';

import { LeaveReviewModal } from './leaveReviewModal';
import type { LeaveReviewProduct, OpenLeaveReviewArgs } from './types';

interface LeaveReviewContextValue {
  /** Hides the prompt until a new product to review appears. */
  dismissPrompt: () => void;
  /** Whether Leave Review is available to this user; entry points render nothing otherwise. */
  isEnabled: boolean;
  /** Whether the modal can open: entry points wait for this instead of opening half-loaded. */
  isReady: boolean;
  openLeaveReview: (args: OpenLeaveReviewArgs) => void;
  /** Most recent purchase first; empty while the prompt is hidden. */
  promptProducts: LeaveReviewProduct[];
  /** Clears the dismissal and refetches, e.g. after checkout. */
  resetPrompt: () => void;
}

const LeaveReviewContext = createContext<LeaveReviewContextValue>({
  dismissPrompt: () => undefined,
  isEnabled: false,
  isReady: false,
  openLeaveReview: () => undefined,
  promptProducts: [],
  resetPrompt: () => undefined,
});

export const useLeaveReview = () => useContext(LeaveReviewContext);

const toFeelingOption = ({ id, label }: ReviewTagOption): ReviewOption => {
  const Icon = FEELING_ICONS[label];
  return {
    icon: Icon ? <Icon color={null} size="md" /> : undefined,
    id,
    label,
  };
};

const readDismissedIds = (cookieName: string): number[] =>
  (getCookieFromDocument(cookieName) ?? '')
    .split(',')
    .filter(Boolean)
    .map(Number);

interface LeaveReviewProviderProps {
  children: ReactNode;
  cookiePrefix: Nullish<string>;
  /** Leave Review flag is on and the user is logged in. */
  enabled: boolean;
  /** The current page shows the prompt (PLP and PDP). */
  showPrompt: boolean;
}

/**
 * Makes the Leave Review modal openable from anywhere below it and decides
 * when the prompt shows. Review question options load with the prompt, so the
 * modal never opens without them.
 */
export const LeaveReviewProvider = ({
  children,
  cookiePrefix,
  enabled,
  showPrompt,
}: LeaveReviewProviderProps) => {
  const tagsFetcher = useFetcher<ActionResult<ReviewTagOptions>>();
  const productsFetcher = useFetcher<ActionResult<LeaveReviewProduct[]>>();
  const hasRequestedTagsRef = useRef(false);
  const requestedProductsVersionRef = useRef<number | null>(null);
  const [productsVersion, setProductsVersion] = useState(0);
  const cookieName = getPromptCookieName(PROMPTS.leaveReview, cookiePrefix);
  // Null on the server; safe on hydration since nothing renders until products
  // load client-side.
  const [dismissedIds, setDismissedIds] = useState<number[] | null>(() =>
    typeof document === 'undefined' ? null : readDismissedIds(cookieName)
  );
  const [openArgs, setOpenArgs] = useState<OpenLeaveReviewArgs | null>(null);

  useEffect(() => {
    if (!enabled || !showPrompt) return;
    if (requestedProductsVersionRef.current === productsVersion) return;
    requestedProductsVersionRef.current = productsVersion;
    productsFetcher.load('/api/unreviewed-products');
  }, [enabled, showPrompt, productsFetcher, productsVersion]);

  const products = useMemo(
    () => (productsFetcher.data?.success ? productsFetcher.data.payload : []),
    [productsFetcher.data]
  );
  const hasUndismissedProducts =
    dismissedIds !== null &&
    products.some(({ id }) => !dismissedIds.includes(id));

  useEffect(() => {
    if (!enabled || !hasUndismissedProducts || hasRequestedTagsRef.current)
      return;
    hasRequestedTagsRef.current = true;
    tagsFetcher.load('/api/product-reviews');
  }, [enabled, hasUndismissedProducts, tagsFetcher]);

  const tagOptions = tagsFetcher.data?.success
    ? tagsFetcher.data.payload
    : null;
  const options = useMemo(
    () =>
      tagOptions && {
        activityOptions: tagOptions.activities,
        feelingOptions: tagOptions.feelings.map(toFeelingOption),
      },
    [tagOptions]
  );
  const isReady = enabled && options !== null;

  const promptProducts = useMemo(
    () =>
      isReady && showPrompt && hasUndismissedProducts && !openArgs
        ? products
        : [],
    [hasUndismissedProducts, isReady, openArgs, products, showPrompt]
  );

  const openLeaveReview = useCallback(
    (args: OpenLeaveReviewArgs) => {
      if (!isReady) return;
      setOpenArgs(args);
    },
    [isReady]
  );

  const dismissPrompt = useCallback(() => {
    const ids = products.map(({ id }) => id);
    // Same lifetime as the login session, so the prompt returns on next login.
    setCookie(cookieName, ids.join(','), durations.ONE_DAY);
    setDismissedIds(ids);
  }, [cookieName, products]);

  const resetPrompt = useCallback(() => {
    setCookie(cookieName, '', 0);
    setDismissedIds([]);
    setProductsVersion((version) => version + 1);
  }, [cookieName]);

  const value = useMemo(
    () => ({
      dismissPrompt,
      isEnabled: enabled,
      isReady,
      openLeaveReview,
      promptProducts,
      resetPrompt,
    }),
    [
      dismissPrompt,
      enabled,
      isReady,
      openLeaveReview,
      promptProducts,
      resetPrompt,
    ]
  );

  return (
    <LeaveReviewContext.Provider value={value}>
      {children}
      {options && openArgs && (
        <ClientOnly>
          {(isMobile) => (
            <LeaveReviewModal
              key={openArgs.product.id} // Remounts if opened for a different product while already open.
              activityOptions={options.activityOptions}
              feelingOptions={options.feelingOptions}
              isMobile={isMobile}
              moreToReview={openArgs.moreToReview ?? []}
              onClose={() => setOpenArgs(null)}
              product={openArgs.product}
              source={openArgs.source}
            />
          )}
        </ClientOnly>
      )}
    </LeaveReviewContext.Provider>
  );
};
